import { useCreateBookmarksMutation } from 'src/graphqlTypes/catalogServiceTypes';
import { isBookmarked, toggleOfferingId, toOfferingHit } from 'src/utilities/offeringHitsUtility';
import { showErrorToast } from 'src/components/Bookmarks/BookmarkErrorToast';
import { showToast } from 'src/components/Bookmarks/BookmarkToast';
import { useBookmarks } from 'src/components/Providers/BookmarksProvider';
import { trackBookmarkRemoved, trackBookmarkSaved } from '@src/services/analytics';

export const useBookmarkActions = () => {
	const { bookmarks, setBookmarks } = useBookmarks();
	const [createBookmarksMutation] = useCreateBookmarksMutation();

	/*
  if a program is bookmarked and then unbookmarked this function is used to
  undo, bookmark the program, via the toast
  */
	const undoRemoveBookmark = async programId => {
		const userBookmarkIds: string[] = bookmarks.map(bkmrk => bkmrk.id);
		const updatedUserBookmarkIds = userBookmarkIds.some(id => id === programId)
			? userBookmarkIds
			: [...userBookmarkIds, programId];
		try {
			const updatedBookmarks = await createBookmarksMutation({
				variables: {
					bookmarks: { programIds: updatedUserBookmarkIds.map(id => parseInt(id)) },
				},
			});

			setBookmarks(updatedBookmarks.data.createBookmarks.map(toOfferingHit));
			trackBookmarkSaved({
				segmentProperties: { offeringID: programId },
			});
		} catch (err) {
			showErrorToast(programId, undoRemoveBookmark);
		}
	};

	/*
  if a program is not bookmarked and then bookmarked this function is used to
  undo, unbookmark the program, via the toast
  */
	const undoAddBookmark = async programId => {
		const userBookmarkIds: string[] = bookmarks.map(bkmrk => bkmrk.id);
		const updatedUserBookmarkIds = userBookmarkIds.some(id => id === programId)
			? userBookmarkIds.filter(id => id !== programId)
			: [...userBookmarkIds];
		try {
			const updatedBookmarks = await createBookmarksMutation({
				variables: {
					bookmarks: { programIds: updatedUserBookmarkIds.map(id => parseInt(id)) },
				},
			});
			setBookmarks(updatedBookmarks.data.createBookmarks.map(toOfferingHit));
			trackBookmarkRemoved({
				segmentProperties: { offeringID: programId },
			});
		} catch (err) {
			showErrorToast(programId, undoAddBookmark);
		}
	};

	const updateBookmark = async programId => {
		const bookmarked = isBookmarked(programId, bookmarks);
		const action = bookmarked ? undoAddBookmark : undoRemoveBookmark;

		try {
			const userBookmarkIds: string[] = bookmarks.map(bkmrk => bkmrk.id);
			const updatedUserBookmarkIds = toggleOfferingId(programId, userBookmarkIds);
			const updatedBookmarks = await createBookmarksMutation({
				variables: {
					bookmarks: { programIds: updatedUserBookmarkIds.map(id => parseInt(id)) },
				},
			});
			if (bookmarked) {
				trackBookmarkRemoved({
					segmentProperties: { offeringID: programId },
				});
				showToast(programId, 'remove', undoRemoveBookmark);
			} else {
				trackBookmarkSaved({
					segmentProperties: { offeringID: programId },
				});
				showToast(programId, 'add', undoAddBookmark);
			}
			await setBookmarks(updatedBookmarks.data.createBookmarks.map(toOfferingHit));
		} catch (err) {
			showErrorToast(programId, action);
		}
	};

	return { undoAddBookmark, undoRemoveBookmark, updateBookmark };
};
